import { Truck, MapPin, ShieldCheck } from "lucide-react";
import { useGeo, flagEmoji } from "@/lib/useGeo";
import { calculateDeliveryWindow } from "@/lib/shipping";

/**
 * Compact "In stock + DHL + geolocated delivery window" strip.
 * Replaces the static "Ships within 24h" row on the PDP buy-box.
 */
export const DispatchInfo = () => {
  const { geo } = useGeo();
  const window = calculateDeliveryWindow();
  const hasCountry = Boolean(geo?.countryCode);
  const countryLabel = hasCountry
    ? `${flagEmoji(geo.countryCode)} ${geo.country}`
    : "🌍 Worldwide";

  return (
    <div
      className="border-t border-neutral-200 pt-3 text-xs space-y-2"
      data-testid="dispatch-info"
    >
      {/* Top row: stock status + DHL + country */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="flex items-center gap-2 text-neutral-500">
          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
          <span className="text-emerald-700 font-bold">In stock</span> · ships within{" "}
          <span className="font-bold text-black">24h</span>
        </span>
        <span
          className="inline-flex items-center gap-1.5 text-neutral-700 font-medium"
          data-testid="dispatch-country"
        >
          <Truck className="w-3.5 h-3.5" strokeWidth={1.75} />
          <span className="font-bold text-black">DHL Express</span>
          <span className="text-neutral-400">·</span>
          <MapPin className="w-3 h-3" />
          <span>{countryLabel}</span>
        </span>
      </div>

      {/* Delivery window band */}
      <div
        className="flex items-center justify-between gap-3 bg-emerald-50/60 border border-emerald-100 px-3 py-2"
        data-testid="dispatch-delivery"
      >
        <span className="text-[10px] tracking-[0.2em] uppercase text-emerald-800 font-bold">
          Estimated delivery
        </span>
        <span className="font-heading font-black tracking-tight text-emerald-900 text-sm">
          {window.label}
        </span>
      </div>

      {/* Country-specific trust reassurance */}
      <div
        className="flex items-start gap-2 text-[11px] leading-snug text-neutral-600 font-light"
        data-testid="dispatch-trust"
      >
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-700" strokeWidth={2} />
        <p>
          We ship to{" "}
          <span className="font-bold text-black">{countryLabel}</span>{" "}
          regularly — current estimates may shift slightly with customs, but in
          our experience parcels arrive within{" "}
          <span className="font-bold text-black">4–5 days</span> of you receiving the tracking number.{" "}
          <span className="text-neutral-500">
            Many happy clinics in {hasCountry ? geo.country : "your region"} already buy from us.
          </span>
        </p>
      </div>
    </div>
  );
};
